import { createAsyncThunk } from '@reduxjs/toolkit';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import {
  createHomeVideoApi,
  deleteHomeVideoApi,
  fetchAllHomeVideosApi,
  fetchCommonSettingsApi,
  fetchHomeObjectiveApi,
  fetchHomeVideosApi,
  fetchLaunchTasksApi,
  fetchStoreAdminViewApi,
  reorderHomeVideosApi,
  resetStoreOverrideApi,
  setLaunchTaskCheckApi,
  setManualValidationApi,
  updateCommonSettingsApi,
  updateHomeVideoApi,
  updateStoreOverrideApi,
} from '../api';
import {
  ChecklistItemType,
  HomeObjectiveSettings,
  HomeVideoInput,
  HomeVideoSection,
  LaunchTaskType,
  StoreHomeObjectiveOverride,
} from '../types';

const tr = (key: string) => i18n.t(`home.${key}`, { ns: 'home' });

type ApiErrorBody = { error?: { status?: string } };

const SAVE_ERROR_KEYS: Record<string, string> = {
  INVALID_YOUTUBE_URL: 'admin.videos.errors.invalidUrl',
  INVALID_HOME_VIDEO_ORDER: 'admin.videos.errors.order',
};

const errorMessage = (error: unknown, fallbackKey: string) => {
  const status = (error as { response?: { data?: ApiErrorBody } })?.response?.data?.error?.status;
  return tr(status && SAVE_ERROR_KEYS[status] ? SAVE_ERROR_KEYS[status] : fallbackKey);
};

const withFeedback = <Arg, Result>(
  type: string,
  call: (arg: Arg) => Promise<Result>,
  { successKey, errorKey }: { successKey?: string; errorKey: string },
) =>
  createAsyncThunk<Result, Arg>(type, async (arg, { dispatch, rejectWithValue }) => {
    try {
      const result = await call(arg);
      if (successKey) {
        dispatch(showMessage({ message: tr(successKey), variant: 'success' }));
      }
      return result;
    } catch (error: unknown) {
      dispatch(showMessage({ message: errorMessage(error, errorKey), variant: 'error' }));
      return rejectWithValue(error);
    }
  });

export const fetchHomeObjectiveThunk = createAsyncThunk('home/fetchObjective', (storeId: string) =>
  fetchHomeObjectiveApi(storeId),
);

export const fetchLaunchTasksThunk = createAsyncThunk('home/fetchLaunchTasks', (storeId: string) =>
  fetchLaunchTasksApi(storeId),
);

export const setLaunchTaskCheckThunk = withFeedback(
  'home/setLaunchTaskCheck',
  ({ storeId, type, checked }: { storeId: string; type: LaunchTaskType; checked: boolean }) =>
    setLaunchTaskCheckApi(storeId, type, checked),
  { errorKey: 'launch.errors.save' },
);

export const fetchHomeVideosThunk = createAsyncThunk('home/fetchVideos', () => fetchHomeVideosApi());

export const fetchCommonSettingsThunk = withFeedback(
  'home/fetchCommonSettings',
  (_: void) => fetchCommonSettingsApi(),
  {
    errorKey: 'admin.errors.load',
  },
);

export const updateCommonSettingsThunk = withFeedback(
  'home/updateCommonSettings',
  (settings: HomeObjectiveSettings) => updateCommonSettingsApi(settings),
  { successKey: 'admin.saved', errorKey: 'admin.errors.save' },
);

export const fetchStoreAdminViewThunk = withFeedback(
  'home/fetchStoreAdminView',
  (storeId: string) => fetchStoreAdminViewApi(storeId),
  { errorKey: 'admin.errors.load' },
);

export const updateStoreOverrideThunk = withFeedback(
  'home/updateStoreOverride',
  ({ storeId, override }: { storeId: string; override: StoreHomeObjectiveOverride }) =>
    updateStoreOverrideApi(storeId, override),
  { successKey: 'admin.saved', errorKey: 'admin.errors.save' },
);

export const resetStoreOverrideThunk = withFeedback(
  'home/resetStoreOverride',
  (storeId: string) => resetStoreOverrideApi(storeId),
  { successKey: 'admin.saved', errorKey: 'admin.errors.save' },
);

export const setManualValidationThunk = withFeedback(
  'home/setManualValidation',
  ({ storeId, type, validated }: { storeId: string; type: ChecklistItemType; validated: boolean }) =>
    setManualValidationApi(storeId, type, validated),
  { errorKey: 'admin.errors.save' },
);

export const fetchAllHomeVideosThunk = withFeedback('home/fetchAllVideos', (_: void) => fetchAllHomeVideosApi(), {
  errorKey: 'admin.errors.load',
});

export const createHomeVideoThunk = withFeedback(
  'home/createVideo',
  (video: HomeVideoInput) => createHomeVideoApi(video),
  { successKey: 'admin.videos.saved', errorKey: 'admin.errors.save' },
);

export const updateHomeVideoThunk = withFeedback(
  'home/updateVideo',
  ({ videoId, video }: { videoId: string; video: Partial<HomeVideoInput> }) => updateHomeVideoApi(videoId, video),
  { successKey: 'admin.videos.saved', errorKey: 'admin.errors.save' },
);

export const deleteHomeVideoThunk = withFeedback(
  'home/deleteVideo',
  async (videoId: string) => {
    await deleteHomeVideoApi(videoId);
    return videoId;
  },
  { successKey: 'admin.videos.deleted', errorKey: 'admin.errors.save' },
);

export const reorderHomeVideosThunk = withFeedback(
  'home/reorderVideos',
  ({ section, videoIds }: { section: HomeVideoSection; videoIds: string[] }) => reorderHomeVideosApi(section, videoIds),
  { errorKey: 'admin.errors.save' },
);
